import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { 
  Wallet as WalletIcon, 
  Plus, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Lock, 
  Unlock, 
  AlertCircle,
  TrendingUp,
  Calendar,
  Search,
  Filter,
  Home,
  FileText,
  Shield
} from 'lucide-react';
import { walletAPI } from '../services/api';
import FundingRequestModal from '../components/FundingRequestModal';
import { useThemeColors } from '../theme/ThemeContext';
import SEO from '../components/SEO';

const Wallet = () => {
  const colors = useThemeColors();
  const [activeTab, setActiveTab] = useState('overview');
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [hasWalletData, setHasWalletData] = useState(false);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const queryClient = useQueryClient();

  // Fetch wallet data
  const {
    data: wallet,
    isLoading: isLoadingWallet,
    refetch: refetchWallet,
    error: walletError,
    status: walletStatus,
  } = useQuery(
    'wallet',
    walletAPI.getWallet,
    {
      retry: false, // Don't retry on 404 (wallet not found)
      select: (response) => {
        // If API returns data, use it; otherwise use dummy data
        const apiData = response?.data;
        if (apiData && apiData.success) {
          return apiData;
        }
      },
      onSuccess: (data) => {  
        console.log('Wallet query success:', data.data);
        setHasWalletData(data.success);
        // refetchWallet();
      },
      onError: (error) => {
        console.log('Wallet query error:', error);
        console.log('Error status:', error.response?.status);
        // Set hasWalletData to true so transactions can load with dummy data
        setHasWalletData(true);
      },
    }
  );

  // Create wallet mutation
  const createWalletMutation = useMutation(walletAPI.createWallet, {
    onSuccess: () => {
      toast.success('Wallet created successfully!');
      refetchWallet();
    },
    onError: (error) => {
      if (error.response?.status === 400 && error.response?.data?.error?.code === 'WALLET_EXISTS') {
        toast.error('Wallet already exists');
        refetchWallet();
      } else {
        toast.error(error.response?.data?.message || 'Failed to create wallet');
      }
    },
  });

  // Fetch transaction history
  const {
    data: transactions,
    isLoading: isLoadingTransactions,
    refetch: refetchTransactions,
  } = useQuery(
    ['transactions', transactionFilter],
    () => walletAPI.getTransactionHistory(50, 0),
    {
      select: (response) => {
        // If API returns data, use it; otherwise use dummy data
        const apiData = response?.data?.data?.transactions || response?.data?.transactions || response?.data?.data || response?.data || [];
        if (Array.isArray(apiData) && apiData.length > 0) {
          return apiData;
        }
      },
      enabled: !!hasWalletData, // Only fetch transactions if wallet exists
    }
  );

  // Fetch wallet locks (disabled for now - endpoint not implemented)
  const locks = [];
  const isLoadingLocks = false;
  const refetchLocks = () => {};

  // Create funding request mutation
  const createFundingRequestMutation = useMutation(walletAPI.createFundingRequest, {
    onSuccess: () => {
      toast.success('Funding request submitted successfully!');
      setShowFundingModal(false);
      queryClient.invalidateQueries('wallet');
      queryClient.invalidateQueries('transactions');
      refetchWallet();
      refetchTransactions();
    },
    onError: (error) => {
      // Handle specific duplicate reference error
      if (error.response?.status === 400 && 
          (error.response?.data?.error?.code === 'DUPLICATE_REFERENCE' ||
           error.response?.data?.message?.includes('duplicate key value violates unique constraint'))) {
        toast.error('A funding request with this reference is already pending. Please use a different reference number.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to submit funding request');
      }
    },
  });

  const handleCreateFundingRequest = (requestData) => {
    createFundingRequestMutation.mutate(requestData);
  };

  const filteredTransactions = transactions?.filter(transaction => {
    if (transactionFilter === 'all') return true;
    return transaction.type === transactionFilter;
  }) || [];

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit':
        return <ArrowDownLeft className="h-4 w-4 text-theme-success" />;
      case 'debit':
        return <ArrowUpRight className="h-4 w-4 text-theme-error" />;
      case 'lock':
        return <Lock className="h-4 w-4 text-theme-warning" />;
      case 'release':
        return <Unlock className="h-4 w-4 text-theme-info" />;
      default:
        return <WalletIcon className="h-4 w-4 text-theme-secondary" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'credit':
      case 'unlock':
        return 'text-theme-success bg-theme-success/10 border-theme-success/20';
      case 'debit':
        return 'text-theme-error bg-theme-error/10 border-theme-error/20';
      case 'lock':
        return 'text-theme-warning bg-theme-warning/10 border-theme-warning/20';
      case 'release':
        return 'text-theme-info bg-theme-info/10 border-theme-info/20';
      default:
        return 'text-theme-secondary bg-theme-neutral-25 border-theme-light';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-theme-background">
      <SEO 
        title="Wallet" 
        description="Manage your funds, track transactions, and handle payments for parcel deliveries on TripTrot"
        keywords={['wallet', 'transaction management', 'fund transfers', 'payment processing', 'parcel delivery payments']}
        canonical="/wallet"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Wallet",
          "description": "User wallet for managing parcel delivery funds and transactions"
        }}
      />
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-theme-primary">Wallet</h1>
        <p className="mt-2 text-theme-secondary">Manage your funds and track transactions</p>
        <div className="mt-4">
          {(() => {
            if (isLoadingWallet) {
              // return (
            }
            else if (hasWalletData) {
              return (
                <button
                  onClick={() => setShowFundingModal(true)}
                  className="flex items-center px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-theme-primary-hover transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Funds
                </button>
                );  
            }
          })()}
        </div>
      </div>

      {/* Wallet Overview */}
      {(() => {
        console.log('Render state:', {
          isLoadingWallet,
          walletStatus,
          walletError,
          hasWalletData: !!wallet?.data?.success,
          wallet: wallet?.data,
          walletErrorStatus: walletError?.response?.status
        });
        
        if (isLoadingWallet) {
          return (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          );
        } else if (!hasWalletData) {
          return (
            // Wallet doesn't exist - show create wallet UI
            <div className="flex flex-col items-center justify-center py-16 bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden">
              {/* Top accent bar */}
              <div className="w-full h-1 bg-gradient-to-r from-theme-warning via-theme-primary to-theme-success" />
              
              <div className="text-center mb-8 px-6">
                <div className="mx-auto w-16 h-16 bg-gradient-to-br from-theme-primary/10 to-theme-primary/5 rounded-xl flex items-center justify-center mb-4 border border-theme-primary/20">
                  <WalletIcon className="h-8 w-8 text-theme-primary" />
                </div>
                <h2 className="text-2xl font-bold text-theme-primary mb-2">No Wallet Found</h2>
                <p className="text-theme-secondary max-w-md">
                  You don't have a wallet yet. Create a wallet to start managing your funds for parcel deliveries.
                </p>
              </div>
              <button
                onClick={() => createWalletMutation.mutate()}
                disabled={createWalletMutation.isLoading}
                className="flex items-center px-6 py-3 bg-theme-primary text-white rounded-lg hover:bg-theme-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createWalletMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Wallet...
                  </>
                ) : (
                  <>
                    <Plus className="h-5 w-5 mr-2" />
                    Create Wallet
                  </>
                )}
              </button>
            </div>
          );
        } else if (hasWalletData) {
          return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Available Balance */}
              <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden">
                {/* Top accent bar */}
                <div className="h-1 bg-gradient-to-r from-theme-success via-theme-primary to-theme-info" />
                
                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-theme-primary">Available Balance</h3>
                    <WalletIcon className="h-5 w-5 text-theme-primary" />
                  </div>
                  <div className="text-3xl font-bold text-theme-primary">
                    {wallet?.data?.available_balance || 0} ETB
                  </div>
                  <p className="text-sm text-theme-secondary mt-2">Ready to use</p>
                </div>
              </div>

              {/* Locked Balance */}
              <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden">
                {/* Top accent bar */}
                <div className="h-1 bg-gradient-to-r from-theme-warning via-theme-primary to-theme-error" />
                
                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-theme-primary">Locked Balance</h3>
                    <Lock className="h-5 w-5 text-theme-warning" />
                  </div>
                  <div className="text-3xl font-bold text-theme-primary">
                    {wallet?.data?.locked_balance || 0} ETB
                  </div>
                  <p className="text-sm text-theme-secondary mt-2">Security deposits</p>
                </div>
              </div>

              {/* Total Balance */}
              <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden">
                {/* Top accent bar */}
                <div className="h-1 bg-gradient-to-r from-theme-primary via-theme-success to-theme-info" />
                
                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-theme-primary">Total Balance</h3>
                    <TrendingUp className="h-5 w-5 text-theme-success" />
                  </div>
                  <div className="text-3xl font-bold text-theme-primary">
                    {parseFloat(wallet?.data?.available_balance || 0) + parseFloat(wallet?.data?.locked_balance || 0)} ETB
                  </div>
                  <p className="text-sm text-theme-secondary mt-2">Available + Locked</p>
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading wallet information...</p>
            </div>
          );
        }
      })()}

          {/* Sleek Tabs */}
          {hasWalletData && (
          <div className="mb-8">
            {/* Mobile: Vertical tabs | Desktop: Horizontal tabs */}
            <div className="sm:hidden mb-6">
              <div className="bg-theme-card border border-theme-light rounded-2xl p-2 overflow-x-hidden">
                {[
                  { key: 'overview', label: 'Overview', icon: Home },
                  { key: 'transactions', label: 'Transactions', icon: FileText },
                  // { key: 'locks', label: 'Locks', icon: Shield }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center w-full px-3 py-3 rounded-xl font-medium text-sm transition-all duration-200 overflow-hidden ${
                      activeTab === tab.key
                        ? 'bg-theme-primary text-white shadow-lg'
                        : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-neutral-25'
                    }`}
                  >
                    <tab.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop version */}
            <div className="hidden sm:block">
              <div className="bg-theme-card border border-theme-light rounded-2xl p-2 flex flex-wrap gap-2 overflow-x-hidden">
                {[
                  { key: 'overview', label: 'Overview', icon: Home },
                  { key: 'transactions', label: 'Transactions', icon: FileText },
                  // { key: 'locks', label: 'Locks', icon: Shield }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center px-3 sm:px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 flex-1 sm:flex-auto min-w-0 overflow-hidden ${
                      activeTab === tab.key
                        ? 'bg-theme-primary text-white shadow-lg'
                        : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-neutral-25'
                    }`}
                  >
                    <tab.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="truncate">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          )}

          {/* Tab Content */}
          {activeTab === 'overview' && hasWalletData && (
            <div className="space-y-6">
              {/* Recent Transactions */}
              <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden">
                {/* Top accent bar */}
                <div className="h-1 bg-gradient-to-r from-theme-primary via-theme-info to-theme-success" />
                
                {/* Content */}
                <div className="p-6 border-b border-theme-light">
                  <h3 className="text-lg font-semibold text-theme-primary">Recent Transactions</h3>
                </div>
                <div className="divide-y divide-theme-light">
                  {transactions?.slice(0, 5).map((transaction) => (
                    <div key={transaction.id} className="p-4 hover:bg-theme-neutral-25 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center min-w-0 flex-1">
                          {getTransactionIcon(transaction.type)}
                          <div className="ml-3 min-w-0 flex-1">
                            <p className="text-sm font-medium text-theme-primary capitalize truncate">
                              {transaction.type}
                            </p>
                            <p className="text-sm text-theme-secondary break-words line-clamp-2">
                              {transaction.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className={`text-sm font-medium ${
                            transaction.type === 'credit' || transaction.type === 'unlock' ? 'text-theme-success' : 'text-theme-error'
                          }`}>
                            {transaction.type === 'credit' || transaction.type === 'unlock' ? '+' : '-'}{transaction.amount} ETB
                          </p>
                          <p className="text-xs text-theme-secondary">
                            {new Date(transaction.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {transactions?.length === 0 && (
                    <div className="p-6 text-center text-theme-secondary">
                      No transactions found
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'transactions' && hasWalletData && (
            <div className="space-y-4">
              {/* Search Card */}
              <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden">
                {/* Top accent bar */}
                <div className="h-1 bg-gradient-to-r from-theme-info via-theme-primary to-theme-success" />
                
                {/* Content */}
                <div className="p-6">
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <div className="relative flex-1 min-w-0">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search transactions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-12 pr-4 py-3 w-full bg-theme-neutral-25 border border-theme-light rounded-xl focus:ring-2 focus:ring-theme-primary/20 focus:border-theme-primary transition-all duration-200"
                      />
                    </div>
                    <div className="relative flex-shrink-0">
                      <select
                        value={transactionFilter}
                        onChange={(e) => setTransactionFilter(e.target.value)}
                        className="w-full min-w-0 px-3 py-3 bg-theme-neutral-25 border border-theme-light rounded-xl focus:ring-2 focus:ring-theme-primary/20 focus:border-theme-primary appearance-none cursor-pointer transition-all duration-200 text-sm"
                      >
                        <option value="all">All Types</option>
                        <option value="credit">Credits</option>
                        <option value="debit">Debits</option>
                        <option value="lock">Locks</option>
                        <option value="unlock">Unlocks</option>
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg className="w-4 h-4 text-theme-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transaction List */}
              {isLoadingTransactions ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden">
                  {/* Top accent bar */}
                  <div className="h-1 bg-gradient-to-r from-theme-primary via-theme-info to-theme-success" />
                  
                  {/* Content */}
                  <div className="divide-y divide-theme-light">
                    {filteredTransactions.map((transaction) => (
                      <div key={transaction.id} className="p-4 hover:bg-theme-neutral-25 transition-colors">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex items-center min-w-0 flex-1">
                            {getTransactionIcon(transaction.type)}
                            <div className="ml-3 min-w-0 flex-1">
                              <p className="text-sm font-medium text-theme-primary capitalize truncate">
                                {transaction.type}
                              </p>
                              <p className="text-sm text-theme-secondary break-words line-clamp-2">{transaction.description}</p>
                              {transaction.reference_id && (
                                <p className="text-xs text-theme-secondary truncate">
                                  Ref: {transaction.reference_type} #{transaction.reference_id}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <p className={`text-sm font-medium ${
                              transaction.type === 'credit' || transaction.type === 'unlock' ? 'text-theme-success' : 'text-theme-error'
                            }`}>
                              {transaction.type === 'credit' || transaction.type === 'unlock' ? '+' : '-'}{transaction.amount} ETB
                            </p>
                            <div className="flex items-center text-xs text-theme-secondary">
                              <Calendar className="h-3 w-3 mr-1" />
                              <span>{new Date(transaction.created_at).toLocaleDateString()}</span>
                            </div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 border ${getTransactionColor(transaction.type)}`}>
                              {transaction.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredTransactions.length === 0 && (
                      <div className="p-6 text-center text-theme-secondary">
                        No transactions found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}


      {/* Funding Request Modal */}
      {showFundingModal && (
        <FundingRequestModal
          onClose={() => setShowFundingModal(false)}
          onSubmit={handleCreateFundingRequest}
          isLoading={createFundingRequestMutation.isLoading}
        />
      )}
    </div>
  );
};

export default Wallet;
